import { useEffect } from 'react';

interface ContentProtectionOptions {
  /** Disable right-click context menu */
  disableContextMenu?: boolean;
  /** Disable text selection */
  disableSelection?: boolean;
  /** Disable keyboard shortcuts (Ctrl+S, Ctrl+P, PrintScreen, etc.) */
  disableKeyboardShortcuts?: boolean;
  /** Disable drag and drop of images/content */
  disableDragDrop?: boolean;
  /** Blur content when window loses focus (helps against some screen capture) */
  blurOnFocusLoss?: boolean;
  /** Add CSS to hide content from screenshots in some browsers */
  preventCapture?: boolean;
}

const defaultOptions: ContentProtectionOptions = {
  disableContextMenu: true,
  disableSelection: true,
  disableKeyboardShortcuts: true,
  disableDragDrop: true,
  blurOnFocusLoss: false, // Can be annoying, disabled by default
  preventCapture: true,
};

/**
 * Hook to add content protection measures to prevent casual screen recording/screenshots.
 * 
 * NOTE: These are deterrents, not foolproof protection. Determined users can bypass these.
 * For true DRM protection on videos, use Cloudflare Stream's built-in DRM features.
 */
export function useContentProtection(options: ContentProtectionOptions = {}) {
  const opts = { ...defaultOptions, ...options };

  useEffect(() => {
    // Prevent right-click context menu
    const handleContextMenu = (e: MouseEvent) => {
      if (opts.disableContextMenu) {
        e.preventDefault();
        return false;
      }
    };

    // Prevent keyboard shortcuts
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!opts.disableKeyboardShortcuts) return;

      // PrintScreen
      if (e.key === 'PrintScreen') {
        e.preventDefault();
        return false;
      }

      // Ctrl/Cmd + S (Save)
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        return false;
      }

      // Ctrl/Cmd + P (Print)
      if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
        e.preventDefault();
        return false;
      }

      // Ctrl/Cmd + Shift + S (Screenshot on some systems)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 's') {
        e.preventDefault();
        return false;
      }

      // Ctrl/Cmd + Shift + I (DevTools)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'i') {
        e.preventDefault();
        return false;
      }

      // F12 (DevTools)
      if (e.key === 'F12') {
        e.preventDefault();
        return false;
      }

      // Ctrl/Cmd + U (View Source)
      if ((e.ctrlKey || e.metaKey) && e.key === 'u') {
        e.preventDefault();
        return false;
      }

      // Ctrl/Cmd + C (Copy) - optional, might be too restrictive
      // if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
      //   e.preventDefault();
      //   return false;
      // }
    };

    // Prevent drag and drop
    const handleDragStart = (e: DragEvent) => {
      if (opts.disableDragDrop) {
        e.preventDefault();
        return false;
      }
    };

    // Blur content when window loses focus
    const handleVisibilityChange = () => {
      if (!opts.blurOnFocusLoss) return;
      
      const protectedElements = document.querySelectorAll('[data-protected]');
      if (document.hidden) {
        protectedElements.forEach(el => {
          (el as HTMLElement).style.filter = 'blur(20px)';
        });
      } else {
        protectedElements.forEach(el => {
          (el as HTMLElement).style.filter = 'none';
        });
      }
    };

    const handleWindowBlur = () => {
      if (!opts.blurOnFocusLoss) return;
      
      const protectedElements = document.querySelectorAll('[data-protected]');
      protectedElements.forEach(el => {
        (el as HTMLElement).style.filter = 'blur(20px)';
      });
    };

    const handleWindowFocus = () => {
      if (!opts.blurOnFocusLoss) return;
      
      const protectedElements = document.querySelectorAll('[data-protected]');
      protectedElements.forEach(el => {
        (el as HTMLElement).style.filter = 'none';
      });
    };

    // Add event listeners
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('dragstart', handleDragStart);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowFocus);

    // Add CSS for selection prevention and capture prevention
    const styleId = 'content-protection-styles';
    let styleEl = document.getElementById(styleId) as HTMLStyleElement;
    
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }

    const styles: string[] = [];

    if (opts.disableSelection) {
      styles.push(`
        [data-protected] {
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
        }
      `);
    }

    if (opts.preventCapture) {
      // These CSS properties can help prevent capture in some scenarios
      // Note: Support varies by browser and OS
      styles.push(`
        [data-protected] {
          -webkit-touch-callout: none;
        }
        
        /* Experimental: Some browsers respect this for screen capture */
        @media (display-mode: standalone) {
          [data-protected] video {
            -webkit-media-controls-panel: none;
          }
        }
      `);
    }

    styleEl.textContent = styles.join('\n');

    // Cleanup
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('dragstart', handleDragStart);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleWindowFocus);
      
      if (styleEl) {
        styleEl.remove();
      }
    };
  }, [opts]);
}


